import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../services/supabaseClient'; 
import { Building, MapPin, Save, CheckSquare, AlertCircle, Plus, Trash2 } from 'lucide-react';

const BUILDING_AMENITIES = [
  { id: 'parking', label: 'Stationnement' },
  { id: 'snow_removal', label: 'Déneigement inclus' },
  { id: 'laundry', label: 'Buanderie' },
];

export default function BuildingForm() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [buildings, setBuildings] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    yearBuilt: '',
    units: '1'
  });

  const [selectedAmenities, setSelectedAmenities] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchBuildings();
  }, []);

  const fetchBuildings = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase.from('habitat_buildings').select('*').eq('landlord_id', user.id);
      if (data) setBuildings(data);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleAmenity = (id: string) => {
    setSelectedAmenities((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Vous devez être connecté.");

      const { error: supabaseError } = await supabase.from('habitat_buildings').insert([{
        landlord_id: user.id,
        name: formData.name || formData.address, // Sécurité si name est vide
        address: `${formData.address}, ${formData.city} ${formData.postalCode}`,
        units_count: parseInt(formData.units) || 1,
      }]);

      if (supabaseError) throw supabaseError;

      setSuccess(true);
      setFormData({ name: '', address: '', city: '', postalCode: '', yearBuilt: '', units: '1' });
      setSelectedAmenities({});
      fetchBuildings();
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'ajout.");
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(false), 3000);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Voulez-vous vraiment supprimer cet immeuble ?")) {
      await supabase.from('habitat_buildings').delete().eq('id', id);
      fetchBuildings();
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="mb-6 border-b border-gray-100 pb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Building className="w-5 h-5 text-teal-600" /> Ajouter un immeuble
          </h2>
        </div>

        {error && <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg flex gap-3 text-sm"><AlertCircle className="w-5 h-5 shrink-0" /><p>{error}</p></div>}
        {success && <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg flex gap-3 text-sm"><CheckSquare className="w-5 h-5 shrink-0" /><p>L'immeuble a été ajouté avec succès !</p></div>}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom du bâtiment (Optionnel)</label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Ex: Le Central" className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse complète</label>
              <input type="text" name="address" required value={formData.address} onChange={handleInputChange} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
              <input type="text" name="city" required value={formData.city} onChange={handleInputChange} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                <input type="text" name="postalCode" required value={formData.postalCode} onChange={handleInputChange} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none uppercase" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nb. Unités</label>
                <input type="number" name="units" required min="1" value={formData.units} onChange={handleInputChange} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Commodités</label>
            <div className="flex gap-3 flex-wrap">
              {BUILDING_AMENITIES.map((amenity) => (
                <label key={amenity.id} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer bg-gray-50 text-sm hover:bg-teal-50 transition-colors">
                  <input type="checkbox" checked={!!selectedAmenities[amenity.id]} onChange={() => toggleAmenity(amenity.id)} className="rounded text-teal-600 focus:ring-teal-500" />
                  {amenity.label}
                </label>
              ))}
            </div>
          </div>

          <button type="submit" disabled={loading} className="bg-slate-900 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-70 w-full md:w-auto">
            {loading ? "Enregistrement..." : <><Plus className="w-5 h-5" /> Enregistrer l'immeuble</>}
          </button>
        </form>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {buildings.map(b => (
          <div key={b.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex justify-between items-center hover:border-teal-300 transition-colors group">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-teal-600 group-hover:bg-teal-50 transition-colors"><Building size={24}/></div>
              <div>
                <h4 className="font-black text-slate-900">{b.name || 'Immeuble'}</h4>
                <p className="text-xs font-bold text-slate-500 flex items-center gap-1 mt-1"><MapPin size={12} className="text-slate-400"/> {b.address}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-teal-600 mt-1">{b.units_count} Unité(s)</p>
              </div>
            </div>
            <button onClick={() => handleDelete(b.id)} className="text-slate-300 hover:text-red-500 p-2 transition-colors"><Trash2 size={18}/></button>
          </div>
        ))}
        {buildings.length === 0 && <div className="col-span-2 text-center text-slate-400 py-10 font-medium">Aucun immeuble enregistré.</div>}
      </div>
    </div>
  );
}
